import { omit } from "lodash-es";

/** Immutable per-cell values keyed by row id, then column. */
export type CellMap<V> = ReadonlyMap<string, Readonly<Record<string, V>>>;

export const getCell = <V>(map: CellMap<V>, rowId: string, column: string) =>
  map.get(rowId)?.[column];

export const hasCell = <V>(map: CellMap<V>, rowId: string, column: string) =>
  column in (map.get(rowId) ?? {});

export const setCell = <V>(
  map: CellMap<V>,
  rowId: string,
  column: string,
  value: V,
): CellMap<V> =>
  getCell(map, rowId, column) === value
    ? map
    : new Map(map).set(rowId, { ...map.get(rowId), [column]: value });

export const deleteCell = <V>(
  map: CellMap<V>,
  rowId: string,
  column: string,
): CellMap<V> => {
  if (!hasCell(map, rowId, column)) return map;
  const remaining = omit(map.get(rowId), column);
  const next = new Map(map);
  if (Object.keys(remaining).length === 0) {
    next.delete(rowId);
  } else {
    next.set(rowId, remaining);
  }
  return next;
};

export const deleteRowCells = <V>(
  map: CellMap<V>,
  rowId: string,
): CellMap<V> => {
  if (!map.has(rowId)) return map;
  const next = new Map(map);
  next.delete(rowId);
  return next;
};

export const copyRowCells = <V>(
  map: CellMap<V>,
  fromRowId: string,
  toRowId: string,
): CellMap<V> => {
  const source = map.get(fromRowId);
  return source ? new Map(map).set(toRowId, { ...source }) : map;
};
